import { GeocodingResult, INDIA_CENTER, INDIA_BOUNDS } from '../types/gaushalaMap.types';
import { isValidAddress } from './dataTransformService';

/**
 * PRIORITY LOCALITIES - Specific areas that should match BEFORE city names
 * These are checked first to ensure accurate locality-level positioning
 */
const PRIORITY_LOCALITIES: Record<string, { latitude: number; longitude: number }> = {
  // Hyderabad/Secunderabad specific areas
  'sp road': { latitude: 17.4400, longitude: 78.4983 },
  's p road': { latitude: 17.4400, longitude: 78.4983 },
  'begumpet': { latitude: 17.4433, longitude: 78.4708 },
  'lakshmi building': { latitude: 17.4400, longitude: 78.4983 }, // SP Road area
  'banjara hills': { latitude: 17.4138, longitude: 78.4397 },
  'jubilee hills': { latitude: 17.4326, longitude: 78.4071 },
  'hitech city': { latitude: 17.4435, longitude: 78.3772 },
  'gachibowli': { latitude: 17.4401, longitude: 78.3489 },
  'kondapur': { latitude: 17.4614, longitude: 78.3540 },
  'madhapur': { latitude: 17.4485, longitude: 78.3908 },
  'ameerpet': { latitude: 17.4374, longitude: 78.4483 },
  'kukatpally': { latitude: 17.4947, longitude: 78.3996 },

  // Bangalore localities
  'koramangala': { latitude: 12.9352, longitude: 77.6245 },
  'indiranagar': { latitude: 12.9784, longitude: 77.6408 },
  'whitefield': { latitude: 12.9698, longitude: 77.7500 },
  'electronic city': { latitude: 12.8399, longitude: 77.6770 },
  'hsr layout': { latitude: 12.9116, longitude: 77.6389 },
  'marathahalli': { latitude: 12.9591, longitude: 77.6971 },

  // Chennai localities
  't nagar': { latitude: 13.0418, longitude: 80.2341 },
  'anna nagar': { latitude: 13.0850, longitude: 80.2101 },
  'adyar': { latitude: 13.0012, longitude: 80.2565 },
  'velachery': { latitude: 12.9815, longitude: 80.2180 },

  // Mumbai localities
  'andheri': { latitude: 19.1136, longitude: 72.8697 },
  'bandra': { latitude: 19.0596, longitude: 72.8295 },
  'colaba': { latitude: 18.9067, longitude: 72.8147 },
  'powai': { latitude: 19.1176, longitude: 72.9060 },
  'worli': { latitude: 19.0176, longitude: 72.8150 },

  // Delhi NCR localities
  'connaught place': { latitude: 28.6315, longitude: 77.2167 },
  'karol bagh': { latitude: 28.6514, longitude: 77.1907 },
  'dwarka': { latitude: 28.5921, longitude: 77.0460 },
  'noida': { latitude: 28.5355, longitude: 77.3910 },
  'sector 18': { latitude: 28.5690, longitude: 77.3217 },

  // Kolkata localities
  'salt lake': { latitude: 22.5800, longitude: 88.4127 },
  'park street': { latitude: 22.5519, longitude: 88.3529 },

  // Gurgaon localities
  'cyber city': { latitude: 28.4941, longitude: 77.0868 },
  'dlf phase': { latitude: 28.4745, longitude: 77.1007 },
  'mg road gurgaon': { latitude: 28.4800, longitude: 77.0835 },

  // Haryana - Jind district localities (PRIORITY)
  'uchana': { latitude: 29.5217, longitude: 76.2547 },
  'uchana sub office': { latitude: 29.5217, longitude: 76.2547 },
  'neemwali': { latitude: 29.3500, longitude: 76.2500 },
  'jind': { latitude: 29.3161, longitude: 76.3147 },
  'behal': { latitude: 29.4200, longitude: 76.3100 },
};

/**
 * Exact coordinates for Indian cities (city-level precision)
 * Used for fallback when geocoding fails
 */
const CITY_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  // Telangana cities
  'hyderabad': { latitude: 17.3850, longitude: 78.4867 },
  'secunderabad': { latitude: 17.4400, longitude: 78.5000 }, // Updated to be closer to Begumpet area
  'begumpet': { latitude: 17.4433, longitude: 78.4708 }, // Correct Begumpet coordinates
  'kamareddy': { latitude: 18.3162, longitude: 78.3389 },
  'nizamabad': { latitude: 19.2741, longitude: 78.1091 },
  'karimnagar': { latitude: 18.4386, longitude: 78.1343 },
  'warangal': { latitude: 17.9689, longitude: 78.6139 },
  'medak': { latitude: 18.7421, longitude: 78.2491 },
  'sangareddy': { latitude: 17.6189, longitude: 78.0892 },
  'sanagareddy': { latitude: 17.6189, longitude: 78.0892 }, // alternate spelling
  'siddipet': { latitude: 18.1017, longitude: 78.8522 },
  'khammam': { latitude: 17.2473, longitude: 80.1514 },
  'nalgonda': { latitude: 17.0575, longitude: 79.2669 },
  'adilabad': { latitude: 19.6641, longitude: 78.5320 },
  'mancherial': { latitude: 18.8700, longitude: 79.4580 },
  'mahbubnagar': { latitude: 16.7488, longitude: 77.9850 },
  'rangareddy': { latitude: 17.2543, longitude: 78.3116 },
  'ongole': { latitude: 14.4426, longitude: 79.6304 },

  // Andhra Pradesh cities
  'bhimavaram': { latitude: 16.5500, longitude: 81.5233 },
  'bhimqvaram': { latitude: 16.5500, longitude: 81.5233 }, // misspelling variant
  'vijayawada': { latitude: 16.5062, longitude: 80.6437 },
  'visakhapatnam': { latitude: 17.6869, longitude: 83.2185 },
  'vishakapatnam': { latitude: 17.6869, longitude: 83.2185 }, // misspelling variant
  'vishakhapatnam': { latitude: 17.6869, longitude: 83.2185 }, // misspelling variant
  'tirupati': { latitude: 13.1939, longitude: 79.8941 },
  'guntur': { latitude: 16.3067, longitude: 80.4365 },
  'nellore': { latitude: 14.4426, longitude: 79.9865 },
  'kakinada': { latitude: 16.9891, longitude: 82.2475 },
  'rajahmundry': { latitude: 17.0005, longitude: 81.8040 },
  'eluru': { latitude: 16.7107, longitude: 81.0952 },

  // Karnataka cities
  'bangalore': { latitude: 12.9716, longitude: 77.5946 },
  'bengaluru': { latitude: 12.9716, longitude: 77.5946 },
  'mysore': { latitude: 12.2958, longitude: 76.6394 },
  'mangalore': { latitude: 12.8628, longitude: 74.8455 },

  // Tamil Nadu cities
  'chennai': { latitude: 13.0827, longitude: 80.2707 },
  'madras': { latitude: 13.0827, longitude: 80.2707 },
  'coimbatore': { latitude: 11.0081, longitude: 76.9558 },
  'salem': { latitude: 11.6643, longitude: 78.1460 },

  // Maharashtra cities
  'mumbai': { latitude: 19.0760, longitude: 72.8777 },
  'pune': { latitude: 18.5204, longitude: 73.8567 },
  'nagpur': { latitude: 21.1458, longitude: 79.0882 },
  'aurangabad': { latitude: 19.8762, longitude: 75.3433 },

  // Delhi & NCR
  'delhi': { latitude: 28.7041, longitude: 77.1025 },
  'new delhi': { latitude: 28.5355, longitude: 77.3910 },

  // Uttar Pradesh cities
  'lucknow': { latitude: 26.8467, longitude: 80.9462 },
  'kanpur': { latitude: 26.4499, longitude: 80.3319 },
  'agra': { latitude: 27.1767, longitude: 78.0081 },

  // West Bengal cities
  'kolkata': { latitude: 22.5726, longitude: 88.3639 },
  'calcutta': { latitude: 22.5726, longitude: 88.3639 },

  // Bihar cities
  'patna': { latitude: 25.5941, longitude: 85.1376 },

  // Rajasthan cities
  'jaipur': { latitude: 26.9124, longitude: 75.7873 },
  'jodhpur': { latitude: 26.2389, longitude: 73.0243 },

  // Gujarat cities
  'ahmedabad': { latitude: 23.0225, longitude: 72.5714 },
  'surat': { latitude: 21.1702, longitude: 72.8311 },
  'vadodara': { latitude: 22.3072, longitude: 73.1812 },

  // Punjab cities
  'chandigarh': { latitude: 30.7333, longitude: 76.7794 },
  'amritsar': { latitude: 31.6340, longitude: 74.8723 },
  'ludhiana': { latitude: 30.9010, longitude: 75.8573 },

  // Haryana cities
  'gurgaon': { latitude: 28.4595, longitude: 77.0266 },
  'gurugram': { latitude: 28.4595, longitude: 77.0266 }, // alternate name
  'faridabad': { latitude: 28.4089, longitude: 77.3178 },
  'jind': { latitude: 29.3161, longitude: 76.3147 },
  'uchana': { latitude: 29.5217, longitude: 76.2547 },
  'uchana sub office': { latitude: 29.5217, longitude: 76.2547 }, // postal variant
  'rohtak': { latitude: 28.8955, longitude: 76.6066 },
  'sonipat': { latitude: 28.9931, longitude: 77.0151 },
  'panipat': { latitude: 29.3909, longitude: 76.9635 },
  'karnal': { latitude: 29.6857, longitude: 76.9905 },
  'hisar': { latitude: 29.1492, longitude: 75.7217 },
  'sirsa': { latitude: 29.5342, longitude: 75.0309 },
  'ambala': { latitude: 30.3752, longitude: 76.7821 },
  'yamunanagar': { latitude: 30.1290, longitude: 77.2674 },
  'neemwali': { latitude: 29.3500, longitude: 76.2500 }, // Neemwali village near Uchana, Jind district
  'haryana': { latitude: 29.0588, longitude: 76.0856 }, // State center fallback
  'behal': { latitude: 29.4200, longitude: 76.3100 }, // Near Jind
  'khatauli': { latitude: 29.2800, longitude: 77.2500 }, // Near Muzaffarnagar
  '126115': { latitude: 29.5217, longitude: 76.2547 }, // Uchana pincode

  // Goa
  'goa': { latitude: 15.2993, longitude: 73.8243 },
  'panaji': { latitude: 15.4909, longitude: 73.8278 },

  // Odisha cities
  'bhubaneswar': { latitude: 20.2961, longitude: 85.8245 },
  'cuttack': { latitude: 20.4625, longitude: 85.8830 },

  // Jharkhand cities
  'jamshedpur': { latitude: 22.8046, longitude: 86.1846 },
  'ranchi': { latitude: 23.3441, longitude: 85.3096 },

  // Madhya Pradesh cities
  'indore': { latitude: 22.7196, longitude: 75.8577 },
  'bhopal': { latitude: 23.2599, longitude: 77.4126 },
  'jabalpur': { latitude: 23.1815, longitude: 79.9864 },
  'gwalior': { latitude: 26.2183, longitude: 78.1828 },
  'ujjain': { latitude: 23.1765, longitude: 75.7885 },
  'rewa': { latitude: 24.5315, longitude: 81.2962 },
  'satna': { latitude: 24.5800, longitude: 80.8322 },
  'sagar': { latitude: 23.8388, longitude: 78.7378 },

  // Assam cities
  'guwahati': { latitude: 26.1445, longitude: 91.7362 },
};

/**
 * Map of Indian states to their center coordinates
 */
const STATE_COORDINATES: Record<string, { latitude: number; longitude: number }> = {
  'Telangana': { latitude: 18.1124, longitude: 79.0193 },
  'Andhra Pradesh': { latitude: 15.9129, longitude: 78.6675 },
  'Karnataka': { latitude: 15.3173, longitude: 75.7139 },
  'Tamil Nadu': { latitude: 11.1271, longitude: 79.0280 },
  'Maharashtra': { latitude: 19.7515, longitude: 75.7139 },
  'Delhi': { latitude: 28.7041, longitude: 77.1025 },
  'Uttar Pradesh': { latitude: 26.8467, longitude: 80.9462 },
  'West Bengal': { latitude: 24.6735, longitude: 88.3063 },
  'Bihar': { latitude: 25.5941, longitude: 85.1376 },
  'Rajasthan': { latitude: 27.0238, longitude: 74.2179 },
  'Gujarat': { latitude: 22.2587, longitude: 71.1924 },
  'Punjab': { latitude: 31.5497, longitude: 74.3436 },
  'Haryana': { latitude: 29.0588, longitude: 77.0745 },
  'Goa': { latitude: 15.2993, longitude: 73.8243 },
  'Odisha': { latitude: 20.9517, longitude: 85.0985 },
  'Jharkhand': { latitude: 23.6102, longitude: 85.2799 },
  'Assam': { latitude: 26.2006, longitude: 92.9376 },
};

/**
 * Extract state from address using city name mapping
 * IMPROVED: Sort cities by length (longest first) to avoid substring mismatches
 */
const extractStateFromAddress = (address: string | null): string => {
  if (!address) return 'Unknown';

  const addressLower = address.trim().toLowerCase();

  const cityToStateMap: Record<string, string> = {
    // Telangana
    'hyderabad': 'Telangana',
    'secunderabad': 'Telangana',
    'begumpet': 'Telangana',
    'kamareddy': 'Telangana',
    'nizamabad': 'Telangana',
    'karimnagar': 'Telangana',
    'warangal': 'Telangana',
    'medak': 'Telangana',
    'sangareddy': 'Telangana',
    'sanagareddy': 'Telangana',
    'siddipet': 'Telangana',
    'khammam': 'Telangana',
    'nalgonda': 'Telangana',
    'adilabad': 'Telangana',
    'mancherial': 'Telangana',
    'mahbubnagar': 'Telangana',
    'rangareddy': 'Telangana',
    // Andhra Pradesh
    'ongole': 'Andhra Pradesh',
    'bhimavaram': 'Andhra Pradesh',
    'bhimqvaram': 'Andhra Pradesh',
    'vijayawada': 'Andhra Pradesh',
    'visakhapatnam': 'Andhra Pradesh',
    'vishakapatnam': 'Andhra Pradesh',
    'vishakhapatnam': 'Andhra Pradesh',
    'tirupati': 'Andhra Pradesh',
    'guntur': 'Andhra Pradesh',
    'nellore': 'Andhra Pradesh',
    'kakinada': 'Andhra Pradesh',
    'rajahmundry': 'Andhra Pradesh',
    'eluru': 'Andhra Pradesh',
    // Karnataka
    'bangalore': 'Karnataka',
    'bengaluru': 'Karnataka',
    'mysore': 'Karnataka',
    'mangalore': 'Karnataka',
    // Tamil Nadu
    'chennai': 'Tamil Nadu',
    'madras': 'Tamil Nadu',
    'coimbatore': 'Tamil Nadu',
    'salem': 'Tamil Nadu',
    // Maharashtra
    'mumbai': 'Maharashtra',
    'pune': 'Maharashtra',
    'nagpur': 'Maharashtra',
    'aurangabad': 'Maharashtra',
    // Delhi
    'delhi': 'Delhi',
    'new delhi': 'Delhi',
    // Uttar Pradesh
    'lucknow': 'Uttar Pradesh',
    'kanpur': 'Uttar Pradesh',
    'agra': 'Uttar Pradesh',
    // West Bengal
    'kolkata': 'West Bengal',
    'calcutta': 'West Bengal',
    // Bihar
    'patna': 'Bihar',
    // Jharkhand
    'ranchi': 'Jharkhand',
    'jamshedpur': 'Jharkhand',
    // Rajasthan
    'jaipur': 'Rajasthan',
    'jodhpur': 'Rajasthan',
    // Gujarat
    'ahmedabad': 'Gujarat',
    'surat': 'Gujarat',
    'vadodara': 'Gujarat',
    // Punjab
    'chandigarh': 'Punjab',
    'amritsar': 'Punjab',
    'ludhiana': 'Punjab',
    // Haryana
    'gurgaon': 'Haryana',
    'gurugram': 'Haryana',
    'faridabad': 'Haryana',
    'jind': 'Haryana',
    'uchana': 'Haryana',
    'rohtak': 'Haryana',
    'sonipat': 'Haryana',
    'panipat': 'Haryana',
    'karnal': 'Haryana',
    'hisar': 'Haryana',
    'sirsa': 'Haryana',
    'ambala': 'Haryana',
    'yamunanagar': 'Haryana',
    'neemwali': 'Haryana',
    // Goa
    'goa': 'Goa',
    'panaji': 'Goa',
    // Odisha
    'bhubaneswar': 'Odisha',
    'cuttack': 'Odisha',
    // Assam
    'guwahati': 'Assam',
  };

  // Sort cities by length (longest first) to avoid substring mismatches
  // E.g., "visakhapatnam" should match before "patna"
  const sortedCities = Object.entries(cityToStateMap).sort((a, b) => b[0].length - a[0].length);
  for (const [city, state] of sortedCities) {
    if (addressLower.includes(city)) {
      return state;
    }
  }

  return 'Unknown';
};

/**
 * Get location coordinates with city-level precision fallback
 * IMPROVED: Priority locality matching FIRST, then city matching
 */
export const getLocationCoordinates = (address: string | null): { latitude: number; longitude: number } => {
  if (!address) return INDIA_CENTER;

  // VALIDATE: Reject gibberish and test data addresses
  if (!isValidAddress(address)) {
    console.log(`[Geocoding] Invalid address detected (gibberish), using India center: "${address}"`);
    return INDIA_CENTER;
  }

  const addressLower = address.trim().toLowerCase();

  // FIRST: Extract state from address (this gives us context)
  const state = extractStateFromAddress(address);
  console.log(`[Geocoding] Extracted state from address: "${state}"`);

  // PRIORITY CHECK: Look for specific localities FIRST (most accurate)
  // These take precedence over general city names
  const priorityLocalities = Object.keys(PRIORITY_LOCALITIES).sort((a, b) => b.length - a.length);
  for (const locality of priorityLocalities) {
    if (addressLower.includes(locality)) {
      const coords = PRIORITY_LOCALITIES[locality];
      if (coords) {
        console.log(`[Geocoding] Found PRIORITY locality: ${locality} → ${coords.latitude}, ${coords.longitude}`);
        return coords;
      }
    }
  }

  // SECOND: Look for cities in CITY_COORDINATES directly (includes all variants)
  // Sort by length (longest first) to avoid substring mismatches
  const allCities = Object.keys(CITY_COORDINATES).sort((a, b) => b.length - a.length);

  for (const city of allCities) {
    if (addressLower.includes(city)) {
      const coords = CITY_COORDINATES[city];
      if (coords) {
        console.log(`[Geocoding] Found city match: ${city} → ${coords.latitude}, ${coords.longitude}`);
        return coords;
      }
    }
  }

  // THIRD: Fall back to state coordinates
  if (state !== 'Unknown' && STATE_COORDINATES[state]) {
    console.log(`[Geocoding] Found state match: ${state} → ${STATE_COORDINATES[state].latitude}, ${STATE_COORDINATES[state].longitude}`);
    return STATE_COORDINATES[state];
  }

  console.log(`[Geocoding] No city/state match found, using India center`);
  return INDIA_CENTER;
};

/**
 * Geocoding Service using Nominatim (OpenStreetMap)
 * Free, no API key required, suitable for MVP/Testing
 *
 * Rate limiting: 1 request per second
 * See: https://nominatim.org/usage_policies.php
 */

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org/search';
const USER_AGENT = 'SAUBHAGYA-Admin-Dashboard/1.0';
const REQUEST_DELAY = 1000; // 1 second between requests

/**
 * Sleep utility for rate limiting
 */
const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Validate if coordinates are within India bounds
 */
const isValidIndianCoordinate = (lat: number, lng: number): boolean => {
  return (
    lat >= INDIA_BOUNDS.minLatitude &&
    lat <= INDIA_BOUNDS.maxLatitude &&
    lng >= INDIA_BOUNDS.minLongitude &&
    lng <= INDIA_BOUNDS.maxLongitude
  );
};

/**
 * Clean and format pincode
 */
const cleanPincode = (pincode: string): string => {
  return pincode.trim().replace(/\s+/g, '');
};

/**
 * Geocode by pincode using Nominatim
 * Most reliable method for India
 */
export const geocodeByPincode = async (pincode: string): Promise<GeocodingResult> => {
  try {
    const cleanedPincode = cleanPincode(pincode);

    if (cleanedPincode.length < 5) {
      throw new Error('Invalid pincode format');
    }

    const searchQuery = `${cleanedPincode}, India`;
    const url = `${NOMINATIM_BASE_URL}?q=${encodeURIComponent(searchQuery)}&format=json&countrycodes=in`;

    const response = await fetch(url, {
      headers: {
        'User-Agent': USER_AGENT,
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const results = await response.json();

    if (!results || results.length === 0) {
      throw new Error('No results found for pincode');
    }

    const result = results[0];
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Validate coordinates are in India
    if (!isValidIndianCoordinate(lat, lng)) {
      throw new Error('Coordinates outside India bounds');
    }

    return {
      latitude: lat,
      longitude: lng,
      confidence: 'high',
      source: 'nominatim_pincode',
      address: result.display_name,
    };
  } catch (error) {
    console.error('Pincode geocoding failed:', error);
    throw error;
  }
};

/**
 * Geocode by full address using Nominatim
 * Good accuracy for complete addresses
 */
export const geocodeByAddress = async (address: string): Promise<GeocodingResult> => {
  try {
    if (!address || address.trim().length === 0) {
      throw new Error('Address is empty');
    }

    const searchQuery = `${address}, India`;
    const url = `${NOMINATIM_BASE_URL}?q=${encodeURIComponent(searchQuery)}&format=json&countrycodes=in`;

    const response = await fetch(url, {
      headers: {
        'User-Agent': USER_AGENT,
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const results = await response.json();

    if (!results || results.length === 0) {
      throw new Error('No results found for address');
    }

    const result = results[0];
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Validate coordinates are in India
    if (!isValidIndianCoordinate(lat, lng)) {
      throw new Error('Coordinates outside India bounds');
    }

    return {
      latitude: lat,
      longitude: lng,
      confidence: 'medium',
      source: 'nominatim_address',
      address: result.display_name,
    };
  } catch (error) {
    console.error('Address geocoding failed:', error);
    throw error;
  }
};

/**
 * Geocode by gaushala name (fallback method)
 * Lower accuracy but helps locate approximately
 */
export const geocodeByName = async (name: string): Promise<GeocodingResult> => {
  try {
    if (!name || name.trim().length === 0) {
      throw new Error('Name is empty');
    }

    const searchQuery = `${name}, India`;
    const url = `${NOMINATIM_BASE_URL}?q=${encodeURIComponent(searchQuery)}&format=json&countrycodes=in`;

    const response = await fetch(url, {
      headers: {
        'User-Agent': USER_AGENT,
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const results = await response.json();

    if (!results || results.length === 0) {
      throw new Error('No results found for name');
    }

    const result = results[0];
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Validate coordinates are in India
    if (!isValidIndianCoordinate(lat, lng)) {
      throw new Error('Coordinates outside India bounds');
    }

    return {
      latitude: lat,
      longitude: lng,
      confidence: 'low',
      source: 'nominatim_name',
      address: result.display_name,
    };
  } catch (error) {
    console.error('Name geocoding failed:', error);
    throw error;
  }
};

/**
 * Get fallback coordinates (India center)
 */
export const getFallbackCoordinates = (): GeocodingResult => {
  return {
    latitude: INDIA_CENTER.latitude,
    longitude: INDIA_CENTER.longitude,
    confidence: 'low',
    source: 'fallback',
    address: 'India (Fallback Location)',
  };
};

/**
 * Geocode with automatic fallback strategy
 * OPTIMIZED: Checks local city coordinates FIRST to avoid slow API calls
 * Only calls external API if local lookup fails
 */
export const geocodeWithFallback = async (
  pincode: string | null,
  address: string | null,
  name: string | null,
  maxRetries: number = 1
): Promise<GeocodingResult> => {
  // OPTIMIZATION: Check local city coordinates FIRST (instant, no API delay)
  // This handles most cases without any network calls
  if (address && address.trim().length > 0) {
    const localCoords = getLocationCoordinates(address);

    // Check if we got valid local coordinates (not India center)
    const isIndiaCenter =
      Math.abs(localCoords.latitude - INDIA_CENTER.latitude) < 0.001 &&
      Math.abs(localCoords.longitude - INDIA_CENTER.longitude) < 0.001;

    if (!isIndiaCenter) {
      console.log(`[Geocoding] Using local coordinates for: "${address}"`);
      return {
        latitude: localCoords.latitude,
        longitude: localCoords.longitude,
        confidence: 'medium',
        source: 'local_city_lookup',
        address: address,
      };
    }
  }

  // Also check if name matches a city
  if (name && name.trim().length > 0) {
    const nameCoords = getLocationCoordinates(name);
    const isIndiaCenter =
      Math.abs(nameCoords.latitude - INDIA_CENTER.latitude) < 0.001 &&
      Math.abs(nameCoords.longitude - INDIA_CENTER.longitude) < 0.001;

    if (!isIndiaCenter) {
      console.log(`[Geocoding] Using local coordinates from name: "${name}"`);
      return {
        latitude: nameCoords.latitude,
        longitude: nameCoords.longitude,
        confidence: 'medium',
        source: 'local_name_lookup',
        address: name,
      };
    }
  }

  // Only if local lookup failed, try external API (with delays)
  // Skip API calls entirely for faster loading - use state coordinates instead
  console.log(`[Geocoding] Local lookup failed, using state fallback for: "${address || name}"`);
  const cityStateCoords = getLocationCoordinates(address || name);
  return {
    latitude: cityStateCoords.latitude,
    longitude: cityStateCoords.longitude,
    confidence: 'low',
    source: 'fallback_city_state',
    address: address || 'Fallback Location',
  };
};

/**
 * Batch geocoding - OPTIMIZED for instant local lookups
 * No longer uses rate limiting since we use local coordinates
 */
export const batchGeocode = async (
  items: Array<{
    pincode?: string | null;
    address?: string | null;
    name?: string | null;
  }>,
  onProgress?: (current: number, total: number) => void
): Promise<GeocodingResult[]> => {
  const results: GeocodingResult[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    try {
      const result = await geocodeWithFallback(
        item.pincode || null,
        item.address || null,
        item.name || null,
        1
      );
      results.push(result);
    } catch (error) {
      console.error(`Batch geocoding failed for item ${i}:`, error);
      results.push(getFallbackCoordinates());
    }

    // Update progress
    if (onProgress) {
      onProgress(i + 1, items.length);
    }

    // No delay needed - using instant local lookups now
  }

  return results;
};

/**
 * Validate if a coordinate is in India
 */
export const isValidIndianCoordinates = (
  latitude: number,
  longitude: number
): boolean => {
  return isValidIndianCoordinate(latitude, longitude);
};

/**
 * Get confidence level based on geocoding source
 */
export const getConfidenceLevel = (source: string): 'high' | 'medium' | 'low' => {
  switch (source) {
    case 'nominatim_pincode':
      return 'high';
    case 'nominatim_address':
      return 'medium';
    case 'nominatim_name':
      return 'low';
    default:
      return 'low';
  }
};
